/**
 * Logging Context
 * Provides logging functionality throughout the application
 * 
 * @author Claude
 */
import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { logService } from '../services/logService';
import { useAuth } from '../hooks/useAuth';

interface LoggingContextType {
  isEnabled: boolean;
  sessionId: string;
}

const LoggingContext = createContext<LoggingContextType | undefined>(undefined);

interface LoggingProviderProps {
  children: ReactNode;
}

export const LoggingProvider: React.FC<LoggingProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    // Log application start
    logService.info('Application started', {
      category: 'app-lifecycle',
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });

    // Log user authentication status
    if (isAuthenticated && user) {
      logService.info('User authenticated', {
        category: 'authentication',
        userId: user.id,
        userEmail: user.email,
      });
    }

    // Setup component error boundary logging
    const originalConsoleError = console.error;
    console.error = (...args) => {
      // Log console errors to aggregation service
      logService.error('Console error', undefined, {
        category: 'console-error',
        args: args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)),
      });
      originalConsoleError.apply(console, args);
    };

    // Cleanup function
    return () => {
      logService.info('Application unmounting', {
        category: 'app-lifecycle',
      });
      console.error = originalConsoleError;
    };
  }, [user, isAuthenticated]);

  // Log user authentication changes
  useEffect(() => {
    if (isAuthenticated && user) {
      logService.info('User session started', {
        category: 'session',
        userId: user.id,
        userEmail: user.email,
      });
    } else if (!isAuthenticated) {
      logService.info('User session ended', {
        category: 'session',
      });
    }
  }, [isAuthenticated, user]);

  const value: LoggingContextType = {
    isEnabled: true,
    sessionId: 'session-id', // This would be generated by the service
  };

  return (
    <LoggingContext.Provider value={value}>
      {children}
    </LoggingContext.Provider>
  );
};

export const useLogging = () => {
  const context = useContext(LoggingContext);
  if (!context) {
    throw new Error('useLogging must be used within a LoggingProvider');
  }
  return context;
};